import {AfterViewInit, Component, HostListener, Inject, OnInit, ViewEncapsulation} from '@angular/core';
import {ActivatedRoute, NavigationStart, Router} from '@angular/router';
import {select, Store} from '@ngrx/store';
import * as _ from 'lodash';
import * as moment from 'moment';
import { Event as NavigationEvent } from '@angular/router';
import {Observable} from 'rxjs';
import { filter, map} from 'rxjs/operators';
import {LoadImpersonableUsersAction, ReloadImpersonableUsersAction} from '../../actions/impersonable-users';
import {ToggleSidebarAction} from '../../actions/layout';
import {LoadMyClientsAction} from '../../actions/my-clients';
import {StartImpersonationAction, StopImpersonationAction} from '../../actions/user-context';
import {LoadUserInfoAction, LogoutAction} from '../../actions/user-info';
import {SCROLL_OFFSET} from '../../consts';
import {BrandSettings} from '../../models/app-settings';
import {Auth0UserInfo} from '../../models/auth0-user-info';
import {Client} from '../../models/client';
import {ExternalLink} from '../../models/external-link';
import {ImpersonableUserVM} from '../../models/impersonable-user.vm';
import {ImpersonateUser} from '../../models/impersonate-user';
import {LayoutType} from '../../models/layout-type.enum';
import {Role} from '../../models/role';
import * as fromShared from '../../reducers';
import {AppSettingsService} from '../../services/app-settings.service';
import {LayoutService} from '../../services/layout.service';
import {Site} from '../../models/site';
import {SubscriptionI} from '../../models/subscription-i';
import {LoadPreferencesAction} from '../../actions/preferences';
import {Order} from '../../../order/models/order';
import {LoadCountriesAction} from '../../actions/countries';
import {LoadCulturesAction, LoadTransportersAction, Office, QuickSearchResult} from '../..';
import {LoadProductsAction} from '../../actions/product';
import {
  PageQuickSearchToAction,
  ChangeQuickSearchPageSizeAction,
  OrderQuickSearchByAction,
  CloseQuickSearchAction,
  GotoPlayerAction, GotoPlayerMultiAction
} from '../../actions/quick-search';
import {environment} from '../../../../environments/environment.prod';
import {LoadProductionLineAction} from '../../actions/production-line';
import {LoadOfficesAction} from '../../actions/offices';
import {L10N_LOCALE, L10nLocale} from 'angular-l10n';
import {LoadServicesAction} from '../../actions/service';
import * as fromRoot from '../../reducers';

@Component({
  selector: 'storever-app-layout',
  templateUrl: './app-layout.component.html',
  styleUrls: ['./app-layout.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AppLayoutComponent implements OnInit, AfterViewInit {
  sidebarClosed$: Observable<boolean>;
  userInfo$: Observable<Auth0UserInfo>;
  impersonableUsers$: Observable<ImpersonableUserVM[]>;
  impersonatedUser$: Observable<ImpersonateUser>;
  roles$: Observable<Role[]>;
  brand$: Observable<BrandSettings>;
  externalLinks$: Observable<ExternalLink[]>;
  dashboardLink$: Observable<string[]>;
  clientLink$: Observable<string[]>;
  ordersLink$: Observable<string[]>;
  reportsLink$: Observable<string[]>;
  companiesLink$: Observable<string[]>;
  shippingsLink$: Observable<string[]>;
  invoicesLink$: Observable<string[]>;
  anomaliesLink$: Observable<string[]>;
  scrollToTopVisible = false;
  copyrightYear: number;
  availableLanguages: string[];
  clientId: string;
  storeCurrentClient$: Observable<Client>;
  breadCrumb$: Observable<string>;
  headerTitle$: Observable<object>;
  dataBreadTitle$: Observable<string>;
  myClients$: Observable<Client[]>;
  mySites$: Observable<Site[]>;
  mySubs$: Observable<SubscriptionI[]>;
  myOrders$: Observable<Order[]>;
  myReport: Object[];
  quickSearchResults$: Observable<QuickSearchResult[]>;
  quickSearchResultsCount$: Observable<number>;
  showQuickSearchResults$: Observable<boolean>;
  quickSearchResultsPage$: Observable<number>;
  quickSearchResultsPageSize$: Observable<number>;
  quickSearchResultsOrderBy$: Observable<string>;
  quickSearchResultsPattern$: Observable<string>;
  currentApplicationVersion: string;
  headerTitle: object;
  office$: Observable<Office[]>;

  get hasExternalLinks$(): Observable<boolean> { return this.externalLinks$.pipe(map(links => links.length > 0)); }

  constructor(private store: Store<fromShared.AppState>,
              private layoutService: LayoutService,
              private router: Router,
              private activatedRoute: ActivatedRoute,
              appSettingsService: AppSettingsService,
              @Inject(L10N_LOCALE) public locale: L10nLocale) {
    this.currentApplicationVersion = environment.appVersion;
   // this.breadCrumb$ = this.store.pipe( select(fromShared.selectors.getRouterDataBread) );
   // this.dataBreadTitle$ = this.store.pipe( select(fromShared.selectors.getRouterDataBreadTitle) );
    this.headerTitle$ = this.store.pipe( select(fromShared.selectors.getHeaderTitle));
    this.sidebarClosed$ = this.store.pipe(select(fromShared.selectors.getSidebarClosed));
    this.office$ = this.store.pipe(select(fromRoot.selectors.getOffices));
    this.myClients$ = this.store.pipe(select(fromShared.selectors.getMyClients));
    this.myOrders$ = this.store.pipe(select(fromShared.selectors.getMyOrders));
    this.mySites$ = this.store.pipe(select(fromShared.selectors.getMySites));
    this.mySubs$ = this.store.pipe(select(fromShared.selectors.getMySubscriptions));
    this.myReport = [{name: 'Sales Dashboard'}, { name: 'Pilot players'}];

    this.userInfo$ = this.store.pipe(select(fromShared.selectors.getUserInfo));
    this.impersonableUsers$ = this.store.pipe(select(fromShared.selectors.getImpersonableUsers));
    this.impersonatedUser$ = this.store.pipe(select(fromShared.selectors.getImpersonateUser));
    this.roles$ = this.store.pipe(select(fromShared.selectors.getMyRoles));
    this.externalLinks$ = this.store.pipe(select(fromShared.selectors.getExternalLinks));

    this.brand$ = appSettingsService.settings$.pipe(map(appSettings => appSettings.brand));

    this.quickSearchResults$ = this.store.select(fromShared.selectors.getQuickSearchResults);
    this.quickSearchResultsCount$ = this.store.select(fromShared.selectors.getQuickSearchCount);
    this.showQuickSearchResults$ = this.store.select(fromShared.selectors.getQuickSearchShowModal);
    const quickSearchFilter$ = this.store.select(fromShared.selectors.getQuickSearchFilter);
    this.quickSearchResultsPage$ = quickSearchFilter$.pipe(map( filterQuick => filterQuick.$page));
    this.quickSearchResultsPageSize$ = quickSearchFilter$.pipe(map( filterQuick => filterQuick.$length));
    this.quickSearchResultsOrderBy$ = quickSearchFilter$.pipe(map( filterQuick => filterQuick.$orderBy));
    this.quickSearchResultsPattern$ = quickSearchFilter$.pipe(map( filterQuick => filterQuick.pattern));

    this.storeCurrentClient$ = this.store.pipe(
      select(fromShared.selectors.getCurrentClient),
      filter(client => client != undefined)
    );

    this.clientLink$ = this.storeCurrentClient$.pipe(
      map(() => ['/', 'clients', 'list'])
    );
    this.dashboardLink$ = this.storeCurrentClient$.pipe(
      map(client => ['/', client.uuid, 'dashboard'])
    );
    this.ordersLink$ = this.storeCurrentClient$.pipe(
      map(() => ['/', 'orders', 'list'])
    );
    this.reportsLink$ = this.storeCurrentClient$.pipe(
      map(() => ['/', 'reports' , 'page'])
    );
    this.shippingsLink$ = this.storeCurrentClient$.pipe(
      map(() => ['/', 'shippings' , 'list'])
    );
    this.companiesLink$ = this.storeCurrentClient$.pipe(
      map(() => ['/', 'companies' , 'list'])
    );
    this.invoicesLink$ = this.storeCurrentClient$.pipe(
      map(() => ['/', 'invoices' , 'list'])
    );
    this.anomaliesLink$ = this.storeCurrentClient$.pipe(
      map(() => ['/', 'anomalies' , 'list'])
    );
    this.dashboardLink$ = this.storeCurrentClient$.pipe(
      map(() => ['/', 'dashboard' , 'sales'])
    );

    this.headerTitle$.subscribe(data => {
      if (data) {
        this.headerTitle = data;
      }
    })
    this.copyrightYear = moment().year();
    this.availableLanguages = ['en'];
  }

  // @ts-ignore
  ngOnInit(): void {
    this.store.pipe(select(fromRoot.selectors.getCurrentClient)).subscribe(data => {
      if (data) {
        this.office$.subscribe(dataO => {
          if (dataO) {
            for (let i = 0; i < dataO.length; ++i) {
              if (data.office === dataO[i].id) {
                if (dataO[i].allowOrderCreation) {
                  const observer = new MutationObserver(() => {
                    const icons = document.querySelectorAll('.fa-exchange');
                    icons.forEach(icon => icon.classList.remove('disabled-icon'));
                  });
                  observer.observe(document.body, { childList: true, subtree: true });
                } else {
                  const observer = new MutationObserver(() => {
                    const icons = document.querySelectorAll('.fa-exchange');
                    icons.forEach(icon => icon.classList.add('disabled-icon'));
                  });
                  observer.observe(document.body, { childList: true, subtree: true });
                }
              }
            }
          }
        })
      }

    })



    this.backBtn();

    this.layoutService.setLayout(LayoutType.Full);
    this.store.dispatch(new LoadUserInfoAction());
    this.store.dispatch(new LoadProductsAction());
    this.store.dispatch(new LoadServicesAction());
    this.store.dispatch(new LoadProductionLineAction());
    this.store.dispatch(new LoadOfficesAction());

    this.cache();


  }
  cache(): void {

    if (!localStorage.getItem('buildVersion') || localStorage.getItem('buildVersion').replace(/"/g, '') !== this.currentApplicationVersion) {
      localStorage.setItem('buildVersion', JSON.stringify(this.currentApplicationVersion));
      window.location.reload(true);
    }

  }
  ngAfterViewInit(): void {
    this.store.dispatch(new LoadMyClientsAction());
    this.store.dispatch(new LoadPreferencesAction());
    this.store.dispatch(new LoadCountriesAction());
    this.store.dispatch(new LoadCulturesAction());
    this.store.dispatch(new LoadTransportersAction());

  }
  toggleSidebar(): void {
    this.store.dispatch(new ToggleSidebarAction());
    this.layoutService.toggleSidebar();
  }
  selectLanguage(language: string): void {
  //  this.locale.setCurrentLanguage(language);
  }
  isInRole$(role: string): Observable<boolean> { return this.roles$.pipe(map(roles => _.findIndex(roles, ['code', role]) > -1)); }
  isRouteActive(commands: any[]): boolean { // tslint:disable-line
    if (!commands) {
      return false;
    }
    return this.router.isActive(this.router.createUrlTree(commands), false);
  }
  quickSearchPageChanged(page: number): void { this.store.dispatch(new PageQuickSearchToAction(page)); }
  quickSearchPageSizeChanged(pageSize: number): void { this.store.dispatch(new ChangeQuickSearchPageSizeAction(pageSize)); }
  quickSearchOrderByChanged(orderBy: string): void { this.store.dispatch(new OrderQuickSearchByAction(orderBy)); }
  closeQuickSearchResults(): void { this.store.dispatch(new CloseQuickSearchAction()); }
  gotoPlayer(player: QuickSearchResult): void {
    this.closeQuickSearchResults();
    if (player.subscriptionCount !== 1) {
      this.store.dispatch(new GotoPlayerMultiAction(player));
    } else {
      this.store.dispatch(new GotoPlayerAction(player));
    }

  }
  impersonate(action: 'Enter'|'Leave', modal: any): void { // tslint:disable-line
    if (action === 'Enter') {
      this.store.dispatch(new LoadImpersonableUsersAction());
      modal.open();
    } else {
      this.store.dispatch(new StopImpersonationAction());
    }
  }
  reloadImpersonableUsers(): void { this.store.dispatch(new ReloadImpersonableUsersAction()); }
  impersonateUser(user: ImpersonateUser): void { this.store.dispatch(new StartImpersonationAction(user)); }
  logout(): void {
    const homePath = this.router.serializeUrl(this.router.createUrlTree(['']));
    const returnTo = `${location.protocol}//${location.host}${homePath}`;
    this.store.dispatch(new LogoutAction(returnTo));
  }
  @HostListener('window:scroll', ['$event'])
  onWindowScroll(): void {
    const scrollFromTop: number = window.pageYOffset || document.documentElement.scrollTop || document.querySelector('body').scrollTop;
    this.scrollToTopVisible = scrollFromTop > SCROLL_OFFSET;
  }
  backBtn(): void {
    const url = [];
    addEventListener('popstate', () => {
      url.pop();
      history.pushState(null, null, url[url.length - 1 ] ? url[url.length - 1 ] : null);

    });

    this.router.events.pipe(filter(          ( event: NavigationEvent ) => {
            return( event instanceof NavigationStart ); }     )
      )
      .subscribe(
        ( event: NavigationStart ) => {
          if (url.length > 1) {
            if (url[url.length - 1 ] !== event.url.split('?')[0].split('#')[0] ) {
              url.push(event.url.split('?')[0].split('#')[0]);
            }
          } else {
            url.push(event.url.split('?')[0].split('#')[0]);
          }
        });
  }
}
